import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteFooter from '../components/Footer';
import '../App.css';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const carouselImages = [
    { src: "/demo1.jpg", alt: "NeutralFit Demo 1", text: "Search new and used clubs" },
    { src: "/demo2.jpg", alt: "NeutralFit Demo 2", text: "Keep track of your equipment and tendencies" },
    { src: "/demo3.jpg", alt: "NeutralFit Demo 3", text: "Get recommendations based on your profile" }
  ];

  // const isLoggedIn = Boolean(localStorage.getItem('token') || sessionStorage.getItem('token'));

  // Handle window resize for responsive carousel
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const trackHomeLink = (label: string, target?: string) => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'home_link_click', {
        link_label: label,
        target: target || 'unknown',
      });
    }
  };

  return (
    <>
      <div className="nf-home">
        {/* Hero Section */}
        <section className="nf-hero">
          <div className="nf-hero-content-bg nf-hero-panel">
            <span className="nf-hero-kicker">The Premier Virtual Golf Fitting</span>
            <h1>
              OPTIMIZE DISTANCE
              <br />
              MAXIMIZE CONTROL
            </h1>
            <p className="nf-hero-subtitle">
              Get unbiased recommendations built for your swing, from driver to putter.
            </p>
            <ul className="nf-hero-benefits">
              <li>Data-driven recommendations</li>
              <li>Swing characteristic analysis</li>
              <li>Find new and used equipment</li>
              <li>Always free to use</li>
            </ul>
            <div className="nf-hero-buttons">
              <button
                onClick={() => {
                  const target = window.innerWidth <= 768 ? '/club-fitting-mobile' : '/club-fitting';
                  trackHomeLink('Get Started', target);
                  navigate(target);
                }}
                className="nf-btn nf-btn-primary nf-btn-lg"
              >
                Get Started
              </button>
              <button
                onClick={() => {
                  trackHomeLink('Learn More', '/learn-more');
                  navigate('/learn-more');
                }}
                className="nf-btn nf-btn-secondary nf-btn-lg"
              >
                Learn More
              </button>
            </div>
            <div className="nf-scroll-indicator" onClick={() => {
              trackHomeLink('Scroll to About', '#nf-about');
              const aboutSection = document.getElementById('nf-about');
              if (aboutSection) aboutSection.scrollIntoView({ behavior: 'smooth' });
            }}>
              <span className="nf-double-v-static" aria-label="Scroll to About Us" role="button">
                <svg width="32" height="48" viewBox="0 0 32 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="8,16 16,24 24,16" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#shadow)"/>
                  <polyline points="8,32 16,40 24,32" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" filter="url(#shadow)"/>
                  <defs>
                    <filter id="shadow" x="-2" y="-2" width="36" height="52">
                      <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.35"/>
                    </filter>
                  </defs>
                </svg>
              </span>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="nf-about" className="nf-features">
          <div className="container">
            <h2 className="nf-section-title">How it works</h2>
            <p className="nf-section-subtitle">
              NeutralFit turns your swing and playing tendencies into clear, unbiased equipment recommendations.
            </p>
            <ul className="nf-features-list">
              <li>
                <h3>Tell us how you play</h3>
                <p>Share your current clubs, tendencies, and distances in a quick guided flow.</p>
              </li>
              <li>
                <h3>Compare to 50,000+ golfers</h3>
                <p>Your profile is benchmarked against thousands of golfers and swing data points.</p>
              </li>
              <li>
                <h3>Get full-bag recommendations</h3>
                <p>See optimized options from driver to putter, including shafts and specs that fit your game.</p>
              </li>
              <li>
                <h3>Explore products with context</h3>
                <p>Look up specific clubs and understand how they will affect distance, height, and control.</p>
              </li>
            </ul>
          </div>
        </section>

  {/* Carousel Section */}
        <section className="nf-carousel">
          <div className="container">
            <h2>See NeutralFit in Action</h2>
            <div style={{
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '10px' : '20px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                {/* Navigation Arrows - hide on mobile for cleaner look */}
                {!isMobile && (
                  <>
                    {/* Left Navigation Arrow */}
                    <button 
                      onClick={() => {
                        trackHomeLink('Carousel Prev', 'carousel');
                        prevSlide();
                      }}
                      style={{
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: '#fff',
                        transition: 'all 0.3s ease',
                        flexShrink: 0
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.9)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      ←
                    </button>
                  </>
                )}

                {/* Image Container */}
                <div className="nf-carousel-container" style={{
                  position: 'relative',
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: '10px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <div style={{
                    display: 'flex',
                    transform: `translateX(-${currentSlide * 100}%)`,
                    transition: 'transform 0.5s ease-in-out'
                  }}>
                    {carouselImages.map((image, index) => (
                      <img 
                        key={index}
                        src={image.src} 
                        alt={image.alt}
                        style={{
                          width: '100%',
                          height: isMobile ? 'auto' : '400px',
                          maxHeight: isMobile ? '300px' : '400px',
                          objectFit: isMobile ? 'cover' : 'contain',
                          flexShrink: 0,
                          backgroundColor: '#f5f5f5'
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Mobile Navigation Arrows - overlay style */}
                  {isMobile && (
                    <>
                      <button 
                        onClick={() => {
                          trackHomeLink('Carousel Prev Mobile', 'carousel');
                          prevSlide();
                        }}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          color: '#fff',
                          zIndex: 1
                        }}
                      >
                        ←
                      </button>
                      <button 
                        onClick={() => {
                          trackHomeLink('Carousel Next Mobile', 'carousel');
                          nextSlide();
                        }}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          color: '#fff',
                          zIndex: 1
                        }}
                      >
                        →
                      </button>
                    </>
                  )}
                </div>

                {!isMobile && (
                  <>
                    {/* Right Navigation Arrow */}
                    <button 
                      onClick={() => {
                        trackHomeLink('Carousel Next', 'carousel');
                        nextSlide();
                      }}
                      style={{
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: '#fff',
                        transition: 'all 0.3s ease',
                        flexShrink: 0
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.9)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      →
                    </button>
                  </>
                )}
              </div>
              
              {/* Dots Indicator */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '15px',
                gap: '8px'
              }}>
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      trackHomeLink(`Carousel Dot ${index + 1}`, 'carousel');
                      goToSlide(index);
                    }}
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: 'none',
                      background: currentSlide === index ? '#333' : 'rgba(0,0,0,0.3)',
                      cursor: 'pointer',
                      transition: 'background 0.3s ease'
                    }}
                  />
                ))}
              </div>
              
              {/* Dynamic Text Display */}
              <div style={{
                textAlign: 'center',
                marginTop: '20px',
                fontSize: '18px',
                fontWeight: '500',
                color: '#333',
                minHeight: '25px',
                transition: 'opacity 0.3s ease'
              }}>
                {carouselImages[currentSlide].text}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="nf-testimonials">
          <div className="container">
            <h2>What Our Users Say</h2>
            <div className="nf-testimonial-block">
              <blockquote>
                "I didn't know my old iron set was meant for higher swing speeds, changing them has helped me a lot with height and distance."
              </blockquote>
              <cite>- Shawn, Handicap 19</cite>
            </div>
            <div className="nf-testimonial-block">
              <blockquote>
                "I was actually most impressed by the systems knowledge for shaft options. I already knew about the problems in my bag but NeutralFit offered some niche solutions"
              </blockquote>
              <cite>- Joe, Handicap +1</cite>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="nf-about">
          <div className="container">
            <h2>About Us</h2>
            <div className="nf-about-block">
              <p>
                NeutralFit is the ultimate online golf fitting tool, designed to help golfers of all levels find the perfect clubs. Unlike other services, we don't push sponsored products—we stay completely unbiased, just like our name suggests.
              </p>
              <p>
                Our platform goes beyond basic questionnaires, delivering personalized recommendations tailored to your unique playing style and performance goals. Say goodbye to one-size-fits-all solutions and hello to a smarter, more accurate way to improve your game.
              </p>
              <p>
                Don't get fitted by a salesperson trying to push their latest products. Use NeutralFit to find new and used clubs that fit your game, or to come up with suggestions for your next in person fitting.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Us Section */}
        <section className="nf-contact">
          <div className="container">
            <h2>Contact Us</h2>
            <p>
              Have questions or need assistance? We're here to help! Reach out to us via email.
            </p>
            <div className="nf-contact-details">
              <p>Email: <a href="mailto:neutralfitgolf@gmail.com">neutralfitgolf@gmail.com</a></p>
            </div>
          </div>
        </section>

        <SiteFooter style={{  margin: '20px', width: '300px', alignSelf: 'center' }} />
      </div>
    </>
  );
};

export default Home;
